import { zip, AsyncZippable } from 'fflate'

import type { BasicDriver } from '@/lib/api/company/drivers/useQueryBasicDrivers'
import type { ImiDeclarationItem } from '../services/useImiDeclarationsService'

const COMBINING_MARKS = new RegExp('[\\u0300-\\u036f]', 'g')

export const normalizeName = (value: string | undefined) =>
  (value || '')
    .normalize('NFD')
    .replace(COMBINING_MARKS, '')
    .toLowerCase()
    // "ł" nie rozklada sie przez NFD, a IMI oddaje nazwisko juz jako "l".
    .replace(/ł/g, 'l')
    .trim()

/** Normalize any supported date string to YYYY-MM-DD for comparisons. */
export const parseDeclarationDate = (value?: string): string | null => {
  if (!value) {
    return null
  }

  const trimmed = value.trim()

  if (/^\d{4}-\d{2}-\d{2}/.test(trimmed)) {
    return trimmed.slice(0, 10)
  }

  const dmyFull = trimmed.match(/^(\d{2})-(\d{2})-(\d{4})$/)
  if (dmyFull) {
    return `${dmyFull[3]}-${dmyFull[2]}-${dmyFull[1]}`
  }

  const dmyShort = trimmed.match(/^(\d{2})-(\d{2})-(\d{2})$/)
  if (dmyShort) {
    const yy = Number(dmyShort[3])
    const year = yy >= 70 ? 1900 + yy : 2000 + yy
    return `${year}-${dmyShort[2]}-${dmyShort[1]}`
  }

  return null
}

export const toIsoDateString = (
  year: number,
  month: number,
  day: number,
): string =>
  `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`

/** True when declaration period overlaps the selected date range (inclusive). */
export const isDeclarationInDateRange = (
  item: Pick<
    ImiDeclarationItem,
    'declarationStartDate' | 'declarationEndDate'
  >,
  rangeStart: string,
  rangeEnd: string,
) => {
  const declStart = parseDeclarationDate(item.declarationStartDate)
  if (!declStart) {
    return true
  }

  const declEnd = parseDeclarationDate(item.declarationEndDate) || declStart
  return declStart <= rangeEnd && declEnd >= rangeStart
}

const sanitizePathPart = (value: string, fallback: string) => {
  const sanitized = value
    .replace(/[\\/:*?"<>|]/g, '_')
    .replace(/\s+/g, '_')
    .trim()
    .toUpperCase()
  return sanitized || fallback
}

export const buildImiPackageFilename = (
  surname: string,
  name: string,
  dateFrom: string,
  dateTo: string,
) =>
  `IMI_${sanitizePathPart(surname, 'NAZWISKO')}_${sanitizePathPart(name, 'IMIE')}_${dateFrom}-${dateTo}.zip`

export const matchDriverForDeclaration = (
  declaration: ImiDeclarationItem,
  drivers: BasicDriver[],
): BasicDriver | null => {
  const fullName = normalizeName(declaration.driverLatinFullName)
  if (!fullName) return null

  for (const driver of drivers) {
    const label = normalizeName(
      driver.fullName?.trim() || `${driver.name ?? ''} ${driver.surname ?? ''}`.trim(),
    )
    const tokens = label.split(/\s+/).filter(Boolean)
    if (tokens.length > 0 && tokens.every((tk) => fullName.includes(tk))) {
      return driver
    }
  }

  return null
}

export type DriverDeclarationGroup = {
  driver: BasicDriver | null
  declarations: ImiDeclarationItem[]
}

export const groupDeclarationsByDriver = (
  declarations: ImiDeclarationItem[],
  drivers: BasicDriver[],
): DriverDeclarationGroup[] => {
  const groupMap = new Map<string, DriverDeclarationGroup>()

  for (const declaration of declarations) {
    const matched = matchDriverForDeclaration(declaration, drivers)
    const key = matched
      ? String(matched.id)
      : normalizeName(declaration.driverLatinFullName) || declaration.declarationId

    const existing = groupMap.get(key)
    if (existing) {
      existing.declarations.push(declaration)
    } else {
      groupMap.set(key, { driver: matched, declarations: [declaration] })
    }
  }

  return Array.from(groupMap.values())
}

export const getDeclarationDateRange = (declarations: ImiDeclarationItem[]) => {
  const starts = declarations
    .map((d) => d.declarationStartDate)
    .filter(Boolean) as string[]
  const ends = declarations
    .map((d) => d.declarationEndDate)
    .filter(Boolean) as string[]

  const dateFrom = starts.length > 0 ? starts.sort()[0] : '0000-00-00'
  const dateTo = ends.length > 0 ? ends.sort().reverse()[0] : dateFrom

  return { dateFrom, dateTo }
}

export const resolveDriverNameParts = (
  group: DriverDeclarationGroup,
): { surname: string; name: string } => {
  if (group.driver?.surname || group.driver?.name) {
    return {
      surname: group.driver.surname ?? '',
      name: group.driver.name ?? '',
    }
  }

  const parts = (group.declarations[0]?.driverLatinFullName ?? '')
    .trim()
    .split(/\s+/)
    .filter(Boolean)

  if (parts.length >= 2) {
    return { name: parts[0], surname: parts.slice(1).join('_') }
  }

  return { surname: parts[0] ?? 'KIEROWCA', name: 'IMIE' }
}

export function createDriverPackageZip(
  pdfs: { filename: string; bytes: Uint8Array }[],
): Promise<Uint8Array> {
  return new Promise((resolve, reject) => {
    const zippable: AsyncZippable = {}
    for (const { filename, bytes } of pdfs) {
      zippable[filename] = [bytes, { level: 0 }]
    }
    zip(zippable, (err, result) => {
      if (err) reject(err)
      else resolve(result)
    })
  })
}

export const downloadBlob = (blob: Blob, filename: string) => {
  const url = window.URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  window.URL.revokeObjectURL(url)
}
